import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { planService } from '../../application/PlanService';
import { PLAN_TYPE } from '../../constants/Subscriptions';
import { GeoFence } from '../../domain/geoFence/GeoFence';
import { Plan } from '../../domain/plan/Plan';
import { PlanStatus } from '../../domain/plan/PlanStatus';
import { Project } from '../../domain/project/Project';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveOrganization } from '../organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../project/useRetrieveActiveProjectGeoFences';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type UseCreateEmptyPlanProps = {
  label: string;
  status: PlanStatus;
  executionDate: Date;
};

type UseCreateEmptyPlanResponseProps = ServiceResponse<
  ({ label, executionDate }: UseCreateEmptyPlanProps) => void,
  string | undefined
>;

// Hooks act as 'Adapter' layer.
const useCreateEmptyPlan = ({ project }: { project?: Project }): UseCreateEmptyPlanResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: organization } = useRetrieveOrganization();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: geoFences } = useRetrieveActiveProjectGeoFences();
  const [planId, setPlanId] = useState<string | undefined>(undefined);

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ label, status, executionDate }: UseCreateEmptyPlanProps): Promise<Plan> => {
      const plan = Plan.create({
        label,
        status,
        execution_date: executionDate,
        project_id: project!.id,
        organization_id: organization!.id,
        geo_fences:
          organization!.subscription?.plan !== PLAN_TYPE.MONKEY
            ? (geoFences ?? []).filter((g: GeoFence) => g.enabled)
            : [],
      });
      return planService.create({ plan, project: activeProject! });
    },
    {
      onSuccess: (plan) => {
        setPlanId(plan.id);
      },
      onError: (err: any) => {
        console.info(`Create Empty Plan Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading: isLoading,
    success: isSuccess,
    data: planId,
    run: ({ label, status, executionDate }: UseCreateEmptyPlanProps) => mutate({ label, status, executionDate }),
  };
};

export default useCreateEmptyPlan;
